// src/Pages/UserVideoLessonDetail/Components/LessonsSidebar/LessonsSidebar.tsx
import React, { FunctionComponent, useState } from "react";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Popover,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

const IconClock = () => <span className="icon-placeholder">🕒</span>;

interface Lesson {
  id: number;
  title: string;
  description: string;
  duration: string;
  videoUrl: string;
  progress: string;
  watchTime: string;
  averageScore: string;
  studyStreak: string;
  transcription: string;
  video_play_time?: number;
  completed?: boolean;
}

interface LessonsSidebarProps {
  lessons: Lesson[];
  currentLesson: Lesson;
  onLessonClick: (lesson: Lesson) => void;
  autoplay?: boolean;
  setAutoplay: React.Dispatch<React.SetStateAction<boolean>>;
}

export const LessonsSidebar: FunctionComponent<LessonsSidebarProps> = ({
  lessons,
  currentLesson,
  onLessonClick,
  autoplay,
  setAutoplay,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filters, setFilters] = useState({
    completed: false,
    inProgress: false,
    notStarted: false,
    sortBy: "title",
    order: "ascending",
  });

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCheckboxChange = (name: keyof typeof filters) => {
    setFilters((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const handleSelectChange = (name: keyof typeof filters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getFilteredLessons = () => {
    let result = [...lessons];

    const { completed, inProgress, notStarted, sortBy, order } = filters;

    // ---- Status Filters ----
    if (completed) {
      result = result.filter(
        (l) => l.video_play_time && l.video_play_time >= 100
      );
      // fully watched
    }

    if (inProgress) {
      result = result.filter(
        (l) =>
          l.video_play_time &&
          l.video_play_time > 0 &&
          l.video_play_time &&
          l.video_play_time < 100
      );
      // partially watched
    }

    if (notStarted) {
      result = result.filter(
        (l) => !l.video_play_time || l.video_play_time === 0
      );
      // untouched videos
    }

    // ---- Sorting ----
    result.sort((a, b) => {
      let valA: any, valB: any;

      switch (sortBy) {
        case "title":
          valA = a.title.toLowerCase();
          valB = b.title.toLowerCase();
          break;
        case "duration":
          valA = Number(a.duration);
          valB = Number(b.duration);
          break;
        case "progress":
          valA = a.video_play_time;
          valB = b.video_play_time;
          break;
        default:
          valA = 0;
          valB = 0;
      }

      if (valA < valB) return order === "ascending" ? -1 : 1;
      if (valA > valB) return order === "ascending" ? 1 : -1;
      return 0;
    });

    return result;
  };

  const filteredLessons = getFilteredLessons();

  // const filteredLessons = lessons.filter((lesson) =>
  //   lesson.title.toLowerCase().includes(searchQuery.toLowerCase())
  // );

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const second = Math.floor(seconds % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${second}`;
  };
  return (
    <div className="lessons-sidebar">
      <div className="lessons-sidebar-header">
        <h4>Lessons ({lessons.length})</h4>
        <div className="search-filter-group">
          <div className="search-container">
            <SearchOutlinedIcon className="search-icon" />
            <input
              type="text"
              placeholder="Search lessons..."
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button className="filter-button" onClick={handleClick}>
            <FilterAltOutlinedIcon />
            Filters
          </button>
          {/* popover start */}
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            className="popup-group"
          >
            <div
              className="group-filter"
              style={{ padding: "16px", width: "250px" }}
            >
              <Typography
                variant="subtitle1"
                gutterBottom
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
                fontSize={"16px"}
                fontWeight={"500"}
              >
                Filters
                <CloseOutlinedIcon
                  onClick={handleClose}
                  sx={{ cursor: "pointer" }}
                />
              </Typography>
              <FormGroup>
                <span
                  className="button-filter-heading"
                  style={{ fontSize: "16px", fontWeight: "500" }}
                >
                  Status
                </span>
                <FormControlLabel
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "14px",
                    },
                  }}
                  control={
                    <Checkbox
                      checked={filters.completed}
                      onChange={() => handleCheckboxChange("completed")}
                    />
                  }
                  label="Completed"
                />
                <FormControlLabel
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "14px",
                    },
                  }}
                  control={
                    <Checkbox
                      checked={filters.inProgress}
                      onChange={() => handleCheckboxChange("inProgress")}
                    />
                  }
                  label="In Progress"
                />
                <FormControlLabel
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "14px",
                    },
                  }}
                  control={
                    <Checkbox
                      checked={filters.notStarted}
                      onChange={() => handleCheckboxChange("notStarted")}
                    />
                  }
                  label="Not Started"
                />
              </FormGroup>

              <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
                <span style={{ fontSize: "16px", fontWeight: "500" }}>
                  Sort By
                </span>
              </Typography>
              <Select
                fullWidth
                value={filters.sortBy}
                onChange={(e) => handleSelectChange("sortBy", e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-input": {
                    padding: "8px 16px",
                  },
                }}
              >
                <MenuItem value="title">Title</MenuItem>
                <MenuItem value="duration">Duration</MenuItem>
                <MenuItem value="progress">Progress</MenuItem>
              </Select>

              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                <span style={{ fontSize: "16px", fontWeight: "500" }}>
                  Order
                </span>
              </Typography>
              <Select
                fullWidth
                value={filters.order}
                onChange={(e) => handleSelectChange("order", e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-input": {
                    padding: "8px 16px",
                  },
                }}
              >
                <MenuItem value="ascending">Ascending</MenuItem>
                <MenuItem value="descending">Descending</MenuItem>
              </Select>
            </div>
          </Popover>
          {/* end */}
        </div>
        <FormControlLabel
          label="Auto-play next lesson"
          className="auto-play-button"
          sx={{
            margin: "0",
            display: "flex",
            width: "100%",
            justifyContent: "flex-end",
          }}
          control={
            <Switch
              checked={autoplay}
              onChange={(e) => {
                setAutoplay(e.target.checked);
              }}
            />
          }
        />
      </div>
      <ul className="lessons-list">
        {filteredLessons.map((lesson) => (
          <li
            key={lesson.id}
            className={lesson.id === currentLesson.id ? "playing" : ""}
            onClick={() => {
              if (lesson.id !== currentLesson.id) {
                onLessonClick(lesson);
              }
            }}
          >
            <div
              className={`lesson-status-icon ${
                lesson.id === currentLesson.id ? "playing" : "pending"
              }`}
            ></div>
            <div className="lesson-details">
              <span className="lesson-title">{lesson.title}</span>
              <span className="lesson-duration">
                <AccessTimeIcon sx={{ height: "12px", width: "12px" }} />{" "}
                {formatTime(Number(lesson.duration))}
              </span>
              {lesson?.video_play_time! > 0 && (
                <div className="duration-bar">
                  <div
                    style={{
                      width: "100%",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "8px",
                      overflow: "hidden",
                      height: "3px",
                    }}
                  >
                    <div
                      className="progress-bar-fill"
                      style={{
                        width: `${parseInt(
                          lesson?.video_play_time !== 0
                            ? String(lesson?.video_play_time)
                            : "-"
                        )}%`,
                        backgroundColor: "#3b82f6",
                        height: "100%",
                        borderRadius: "8px",
                        transition: "width 0.3s ease-in-out",
                      }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
